import { customElement, property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { LitElement, html, css } from "lit";
import { PresetController } from "../helper/controllers/preset-controller";

export type SkyColAlign = "stretch" | "start" | "center" | "end" | "baseline";
export type SkyColJustify =
  | "start"
  | "center"
  | "end"
  | "space-between"
  | "space-around"
  | "space-evenly";

/**
 * @element sky-col
 *
 * @summary Grid/flex column helper with responsive spans and configurable alignment/justification.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/grid
 *
 * @slot title - Optional title content shown before main content.
 * @slot - Default content for the column body.
 *
 * @csspart col - The column container.
 *
 * @property {number} span - Default column span.
 * @property {string} gap - Internal content gap CSS value.
 * @property {SkyColJustify} justify - Content justification within the column.
 * @property {SkyColAlign} align - Content alignment within the column.
 * @property {number | undefined} start - Optional explicit grid column start.
 * @property {number | undefined} end - Optional explicit grid column end.
 * @property {number | undefined} sm - Responsive span for small breakpoint.
 * @property {number | undefined} md - Responsive span for medium breakpoint.
 * @property {number | undefined} lg - Responsive span for large breakpoint.
 * @property {number | undefined} xl - Responsive span for extra-large breakpoint.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 * @method refreshLayout Reapplies internal CSS variables from current properties.
 *
 * @Behavior
 * - Applies span and responsive spans through host CSS variables.
 * - Supports explicit `start`/`end` grid positioning when provided.
 * - Automatically updates layout variables when relevant properties change.
 *
 * @example
 * ```html
 * <sky-col span="4" md="6" justify="space-between" align="center">
 *   <span slot="title">Revenue</span>
 *   Q2 performance details
 * </sky-col>
 * ```
 * ```vue
 * <template>
 *   <sky-col span="4" md="6" justify="space-between" align="center">
 *     <span slot="title">Revenue</span>
 *     Q2 performance details
 *   </sky-col>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-col span="4" md="6" justify="space-between" align="center">
 *       <span slot="title">Revenue</span>
 *       Q2 performance details
 *     </sky-col>
 *   );
 * }
 * ```
 */
@customElement("sky-col")
export class SkyCol extends LitElement {
  @property({ type: Number }) span = 1;
  @property({ type: String }) gap = "0";
  @property({ type: String }) justify: SkyColJustify = "center";
  @property({ type: String }) align: SkyColAlign = "stretch";

  @property({ type: Number }) start?: number;
  @property({ type: Number }) end?: number;

  @property({ type: Number }) sm?: number;
  @property({ type: Number }) md?: number;
  @property({ type: Number }) lg?: number;
  @property({ type: Number }) xl?: number;


  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static override styles = css`
    :host {
      display: contents;
    }

    .col {
      display: flex;
      flex-direction: column;
      grid-column: span var(--col-span, 1);
      gap: var(--col-gap, 0);
      align-items: var(--col-align, stretch);
      justify-content: var(--col-justify, center);
    }

    ::slotted([slot="title"]) {
      font-weight: bold;
      margin-bottom: 0.5rem;
    }

    @media (max-width: 768px) {
      .col {
        grid-column: span var(--col-span-sm, var(--col-span, 1));
      }
    }
    @media (max-width: 992px) {
      .col {
        grid-column: span var(--col-span-md, var(--col-span, 1));
      }
    }
    @media (max-width: 1200px) {
      .col {
        grid-column: span var(--col-span-lg, var(--col-span, 1));
      }
    }
    @media (min-width: 1201px) {
      .col {
        grid-column: span var(--col-span-xl, var(--col-span, var(--col-span, 1)));
      }
    }
  `;

  override firstUpdated(): void {
    this.syncVars();
  }

  override updated(changed: Map<string | number | symbol, unknown>): void {
    if (
      changed.has("span") ||
      changed.has("gap") ||
      changed.has("justify") ||
      changed.has("align") ||
      changed.has("sm") ||
      changed.has("md") ||
      changed.has("lg") ||
      changed.has("xl") ||
      changed.has("start") ||
      changed.has("end")
    ) {
      this.syncVars();
    }
  }

  /**
   * Reapplies internal CSS variables from current properties.
   *
   * @returns {void}
   */
  public refreshLayout(): void {
    this.syncVars();
  }

  private syncVars(): void {
    this.style.setProperty("--col-span", String(this.span));
    this.style.setProperty("--col-gap", this.gap);
    this.style.setProperty("--col-justify", this.justify);
    this.style.setProperty("--col-align", this.align);

    if (this.sm) this.style.setProperty("--col-span-sm", String(this.sm));
    if (this.md) this.style.setProperty("--col-span-md", String(this.md));
    if (this.lg) this.style.setProperty("--col-span-lg", String(this.lg));
    if (this.xl) this.style.setProperty("--col-span-xl", String(this.xl));

    if (this.start) this.style.setProperty("--col-start", String(this.start));
    if (this.end) this.style.setProperty("--col-end", String(this.end));
  }

  override render() {
    const gridStyle =
      this.start || this.end
        ? `grid-column: ${this.start ?? "auto"} / ${this.end ?? "auto"};`
        : "";

    return html`
      <div class="col" style=${when(this.start || this.end, () => gridStyle, () => "")} part="col">
        <slot name="title"></slot>
        <slot></slot>
      </div>
    `;
  }
}